import type { GetAutomationRunResponseBody } from "@glide/common-core";
import * as React from "react";
import { formatDuration } from "./lib";
import "twin.macro";
import { JSONViewer } from "../json-viewer/json-viewer";
import {
    browserTimeZone,
    DateFormat,
    formatDateTime,
    GlideJSON,
    isGlideJSONDocumentData,
    TimeFormat,
} from "@glide/data-types";
import { isUndefinedish } from "@glide/support";
import { CopyDataButton } from "./step-logs";
interface OverviewSectionProps {
    run: GetAutomationRunResponseBody["run"];
    // We use this to diferentiate between Integration trigger kinds
    triggerTitle?: string;
}

function getTriggerTitle(run: GetAutomationRunResponseBody["run"], triggerTitle?: string): string {
    if (run.triggerKind === "Integration") {
        return triggerTitle ?? run.triggerKind;
    }

    return run.triggerKind;
}
export const LogsOverviewSection: React.FC<OverviewSectionProps> = p => {
    const { run, triggerTitle } = p;
    const duration = React.useMemo(() => {
        const time = new Date(run?.finishedAt ?? "").getTime() - new Date(run.startedAt).getTime();
        return formatDuration(time / 1000);
    }, [run?.finishedAt, run.startedAt]);

    const formatedStartedAt = React.useMemo(() => {
        return (
            formatDateTime(
                new Date(run.startedAt),
                DateFormat.Medium,
                TimeFormat.WithoutSeconds,
                browserTimeZone(),
                true
            ) ?? ""
        );
    }, [run.startedAt]);

    return (
        <div tw="flex flex-col gap-4 text-text-base">
            <LogOverviewItem title="Trigger" value={getTriggerTitle(run, triggerTitle)} />
            <LogOverviewItem title="Duration" value={duration} />
            <LogOverviewItem title="Started At" value={formatedStartedAt} />
            <LogOverviewItem title="Steps" value={run.steps.toString()} />
            <LogOverviewData value={run.triggerData} />
        </div>
    );
};

interface LogOverviewItemProps {
    readonly title: string;
    readonly value: string;
}

const LogOverviewItem: React.FC<LogOverviewItemProps> = p => {
    const { title, value } = p;
    return (
        <div tw="flex justify-between">
            <span>{title}</span>
            <span>{value}</span>
        </div>
    );
};

const LogOverviewData: React.FC<{ value: unknown }> = p => {
    const { value } = p;
    const parsedValue = React.useMemo(() => {
        if (value === undefined) {
            return undefined;
        }
        if (value instanceof GlideJSON) {
            return value.jsonValue;
        }
        if (isGlideJSONDocumentData(value)) {
            return GlideJSON.fromDocumentData(value).jsonValue;
        }

        if (typeof value === "string") {
            try {
                return JSON.parse(value);
            } catch {
                //
            }
        }

        return value;
    }, [value]);

    if (parsedValue === undefined) return null;

    const isObject = typeof parsedValue === "object";

    if (isUndefinedish(parsedValue) || (isObject && Object.keys(parsedValue).length === 0)) {
        return (
            <div>
                <div tw="mb-2">Data</div>
                <div tw="text-builder-sm text-text-pale">No data available for this trigger</div>
            </div>
        );
    }

    return (
        <div>
            <div tw="flex justify-between items-center">
                <div>Data</div>
                <div tw="flex items-center gap-1">
                    {isObject ? null : <div tw="font-mono">{parsedValue}</div>}
                    <CopyDataButton data={parsedValue} />
                </div>
            </div>
            {isObject ? (
                <div tw="overflow-x-auto mt-2">
                    <JSONViewer json={parsedValue} />
                </div>
            ) : null}
        </div>
    );
};
